import { useState } from 'react'
import axios from 'axios'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage } from '@/lib/api'
import { formatMoney } from '@/lib/format'
import { usePlans, useSetTeamPlan } from '@/lib/queries'
import type { TeamSummary } from '@/lib/types'

const FREE = 'free'

/** yyyy-mm-dd of tomorrow, the earliest day a grant may end. */
const tomorrow = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** Last moment of the chosen local day, so "until 31 Dec" includes the 31st. */
const endOfDay = (date: string) => new Date(`${date}T23:59:59`).toISOString()

export function PlanDialog({ team, open, onOpenChange }: { team: TeamSummary; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { data: plans, isPending } = usePlans()
  const save = useSetTeamPlan()
  const [planId, setPlanId] = useState(team.plan_id)
  const [until, setUntil] = useState(team.subscription.source === 'manual' && team.subscription.expires_at ? team.subscription.expires_at.slice(0, 10) : '')
  const [note, setNote] = useState('')
  const [conflict, setConflict] = useState('')

  const chosen = plans?.find((p) => p.id === planId)
  const removing = planId === FREE
  const hasStripe = team.subscription.source === 'stripe' && !!team.subscription.stripe_subscription_id && team.subscription.state !== 'free'

  async function submit(force: boolean) {
    try {
      await save.mutateAsync({ teamId: team.id, planId, expiresAt: removing || !until ? null : endOfDay(until), note: note.trim(), force })
      toast.success(removing ? 'The team is on the Free plan' : `The team is on ${chosen?.name ?? planId}`)
      onOpenChange(false)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) setConflict(errorMessage(err))
      else toast.error(errorMessage(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change plan</DialogTitle>
          <DialogDescription>
            Puts {team.name || `team ${team.id}`} on a plan without going through Stripe. The change applies on the team&apos;s next request.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="plan">Plan</Label>
            <Select value={planId} onValueChange={(v) => { setPlanId(v); setConflict('') }} disabled={isPending}>
              <SelectTrigger id="plan" className="w-full">
                <SelectValue placeholder="Choose a plan" />
              </SelectTrigger>
              <SelectContent>
                {(plans ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {p.price_cents > 0 ? ` · ${formatMoney(p.price_cents)}` : ''}
                    {p.public ? '' : ' · not for sale'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosen && !removing && (
              <p className="text-xs text-muted-foreground">Features: {chosen.features.includes('*') ? 'all of them' : chosen.features.map((f) => f.replace(/_/g, ' ')).join(', ')}</p>
            )}
          </div>

          {removing ? (
            <p className="text-sm text-muted-foreground">The subscription is removed and the team goes back to Free.</p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="until">Until</Label>
                <Input id="until" type="date" min={tomorrow()} value={until} onChange={(e) => setUntil(e.target.value)} />
                <p className="text-xs text-muted-foreground">{until ? 'The team falls back to Free after this day.' : 'Leave empty to keep the plan until someone changes it.'}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="note">Note</Label>
                <Textarea id="note" maxLength={255} placeholder="Why, and who asked (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
            </>
          )}

          {team.subscription.source === 'trial' && team.subscription.state !== 'expired' && !removing && (
            <p className="rounded-lg border p-3 text-sm text-muted-foreground">
              This team is on its Premium trial. Saving replaces the trial with this plan, given by hand.
            </p>
          )}

          {(conflict || hasStripe) && (
            <p className="rounded-lg border border-warn/40 bg-warn/10 p-3 text-sm">
              {conflict || 'This team pays through Stripe.'} Stripe keeps charging the card, and its next renewal puts the Stripe plan back.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={isPending || save.isPending || (planId === team.plan_id && !until && team.subscription.source === 'stripe')} onClick={() => submit(!!conflict)}>
            {conflict ? 'Replace the Stripe subscription' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
